import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { color } from "../templates/ui";
import InputItem from "../atoms/InputItem";
import Paragraph from "../atoms/Paragraph";
import Button from "../atoms/Button";
import ArrowRight from "../atoms/icons/ArrowRight";
import InputTextArea from "../atoms/InputTextArea";
import InputRadio from "../atoms/InputRadio";
import { useDispatch, useSelector } from "react-redux";
import { sendContactRequest } from "../../services/contactService";
import FileUploader from "../molecules/FileUploader";
import ErrorNotice from "../atoms/ErrorNotice";
import { useTranslation } from "react-i18next";

const StyledParagraph = styled(Paragraph)`
  margin-bottom: 20px;
`;

const StyledErrorNotice = styled(ErrorNotice)`
  margin-bottom: 20px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const StyledArrowRight = styled(ArrowRight)`
  width: 15px;
  height: 11px;
  fill: white;
`;

const StyledTitle = styled.span`
  display: block;
  color: ${props => props.theme.tertiary};
  font-weight: 700;
  margin-bottom: 10px;
`;

const StyledFileUploaderWrapper = styled.div`
  margin-bottom: 20px;
`;

const StyledInputRadio = styled(InputRadio)`
  display: block;
  margin-bottom: 4px;
`;

const StyledInputWrapper = styled.div`
  margin-bottom: 30px;
`;

const RandomQuestionModal = ({ onRequestClose, objectionIsEnabled }) => {
  const { t } = useTranslation();
  const { dossier } = useSelector(state => state.data);

  const [questionState, setQuestionState] = useState({
    dossier: dossier.number,
    client_name: dossier.debtor.salutation,
    subject: "",
    question: "",
    email: "",
    telephone: "",
    files: {},
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = e => {
    setLoading(true);
    setErrors({});
    e.preventDefault();

    const formData = new FormData();

    for (let i = 0; i < questionState.files.length; i++) {
      formData.append("files[]", questionState.files[i]);
    }

    Object.keys(questionState).forEach(
      key => key !== "files" && formData.append(key, questionState[key])
    );

    sendContactRequest(formData)
      .then(response => {
        setLoading(false);
        setSuccess(true);
      })
      .catch(err => {
        setLoading(false);
        setErrors(err.errors);
      });
  };

  const handleChange = e => {
    setQuestionState({ ...questionState, [e.target.name]: e.target.value });
  };

  const handleFileChange = useCallback(
    files => {
      setQuestionState({ ...questionState, files });
    },
    [questionState, setQuestionState]
  );

  return (
    <>
      {!success ? (
        <>
          <StyledParagraph>
            {t("generic_question.introduction")}
          </StyledParagraph>
          {errors.generic_error && (
            <StyledErrorNotice>{errors.generic_error}</StyledErrorNotice>
          )}

          <form onSubmit={submit} encType="multipart/form-data">
            <StyledFieldset>
              <legend>
                <StyledTitle>{t("generic_question.subject")}</StyledTitle>
              </legend>
              <StyledInputWrapper>
                <StyledInputRadio
                  id="question"
                  name="subject"
                  disabled={loading}
                  value={t("generic_question.choice_question")}
                  label={t("generic_question.choice_question", {
                    company_name: t("company_name"),
                  })}
                  onChange={handleChange}
                  required
                />
                {!objectionIsEnabled && (
                  <StyledInputRadio
                    id="objection"
                    name="subject"
                    disabled={loading}
                    value={t("generic_question.choice_objection")}
                    label={t("generic_question.choice_objection")}
                    onChange={handleChange}
                    required
                  />
                )}
                <StyledInputRadio
                  id="payment"
                  name="subject"
                  disabled={loading}
                  value={t("generic_question.choice_payment")}
                  label={t("generic_question.choice_payment")}
                  onChange={handleChange}
                  required
                />
              </StyledInputWrapper>
            </StyledFieldset>
            <InputTextArea
              id="question"
              name="question"
              disabled={loading}
              floatingLabel
              label={t("generic_question.question", {
                company_name: t("company_name"),
              })}
              error={errors["question"]}
              onChange={handleChange}
            />
            <InputItem
              id="email"
              name="email"
              disabled={loading}
              type="email"
              floatingLabel
              error={errors["email"]}
              label={t("generic_question.email")}
              onChange={handleChange}
            />
            <InputItem
              id="telephone"
              error={errors["telephone"]}
              name="telephone"
              disabled={loading}
              floatingLabel
              label={t("generic_question.phone_number")}
              onChange={handleChange}
            />
            <StyledTitle>{t("generic_question.file")}</StyledTitle>
            <StyledFileUploaderWrapper>
              <FileUploader
                error={errors["files.0"]}
                onChange={handleFileChange}
                disabled={loading}
              />
            </StyledFileUploaderWrapper>
            <ButtonWrapper>
              <Button
                loading={loading ? 1 : 0}
                secondary
                reversed
                icon={<StyledArrowRight />}
              >
                {t("generic_question.send")}
              </Button>
            </ButtonWrapper>
          </form>
        </>
      ) : (
        <>
          <StyledParagraph>{t("generic_question.thank")}</StyledParagraph>
          <Button primary wide onClick={onRequestClose}>
            {t("generic.close")}
          </Button>
        </>
      )}
    </>
  );
};

export default RandomQuestionModal;
